/* UploadSummaryVeilRenderer.js */

"use strict";

const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const _ = require("lodash");

/**
 * UploadSummaryVeilRenderer
 *
 * Renders an upload summary view for the offline discovery upload process
 *
 * @listens UploadSummaryVeilComponent#event:Shutdown
 * @fires UploadSummaryVeilComponent#event:Ok
 */
class UploadSummaryVeilRenderer {
    /**
     * Constructor for the UploadSummaryVeilRenderer
     * @param {Object} model - The model for the component
     */
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("import-summary-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._titlePanel = document.createElement("h2");
        this._titlePanel.classList.add("summary-title");

        this._summaryPanel = document.createElement("div");
        this._summaryPanel.classList.add("summary");

        this._logPanel = document.createElement("div");
        this._logPanel.classList.add("import-log");

        this._logLabel = document.createElement("div");
        this._logLabel.innerHTML = "Import Log:";
        this._logLabel.classList.add("log-label");

        this._logList = document.createElement("div");

        this._logPanel.appendChild(this._logList);

        this._fillerPanel = document.createElement("div");
        this._fillerPanel.classList.add("filler");

        this._buttonBar = document.createElement("div");
        this._buttonBar.classList.add("summary-buttons");

        this._okButton = new ButtonControl({
            label: "OK",
            darkTheme: true,
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * Register all the listeners for the component
     */
    _registerListeners() {
        this._okButton.on("UserClick", (this._onOkClick = this._onOkClick.bind(this)));
    }

    /**
     * Unregister all the listeners for the component
     */
    _unregisterListeners() {
        this._okButton.removeAllListeners("UserClick");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Shutdown the component
     * @returns {Promise} A promise that is resolved when the component has finished shutting down
     */
    async shutdown() {
        this._unregisterListeners();
        await this._okButton.shutdown();
        this._summaryPanel = undefined;
        this._logPanel = undefined;
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titlePanel);
                this._rootElem.appendChild(this._summaryPanel);
                this._rootElem.appendChild(this._logLabel);
                this._rootElem.appendChild(this._logPanel);
                this._rootElem.appendChild(this._fillerPanel);
                this._rootElem.appendChild(this._buttonBar);
                this._buttonBar.appendChild(this._okButton.renderer.rootElement);
                this._displaySummary();
            }
            resolve();
        });
    }

    /**
     * Get the root element of the component
     * @returns {HTMLElement} The root element of the component
     */
    get rootElement() {
        return this.getRootElement();
    }

    /**
     * Get the root element of the component
     * @returns {HTMLElement} The root element of the component
     */
    getRootElement() {
        return this._rootElem;
    }

    /**
     * Get the model for the component
     * @returns {Object} The model for the component
     */
    get model() {
        return this.getModel();
    }

    /**
     * Get the model for the component
     * @returns {Object} The model for the component
     */
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /**
     * Display the summary of the import
     */
    _displaySummary() {
        const {
            discoveredCnt,
            discoveryFileCnt,
            failureCount,
            multipleMatchCnt,
            noMacCnt,
            noMatchCnt,
        } = this._model.importResults.summary;

        let title = "Discovery Result Import Completed";
        let summary = _.get(this.model, "importResults.summary", {});
        let results = summary.results || [];

        this._titlePanel.innerHTML = title;

        let discoveredLabel = document.createElement("span");
        discoveredLabel.innerText = `Devices successfully updated: ${discoveredCnt || 0}`;
        this._summaryPanel.appendChild(discoveredLabel);

        let importedLabel = document.createElement("span");
        importedLabel.innerText = `Discovery result files imported: ${discoveryFileCnt || 0}`;
        this._summaryPanel.appendChild(importedLabel);

        let failedLabel = document.createElement("span");
        failedLabel.innerText = `Files that did not update any devices: ${failureCount || 0}`;
        this._summaryPanel.appendChild(failedLabel);

        let multipleMatchesLabel = document.createElement("span");
        multipleMatchesLabel.innerText = `Files with multiple device matches: ${
            multipleMatchCnt || 0
        }`;
        this._summaryPanel.appendChild(multipleMatchesLabel);

        let skippedLabel = document.createElement("span");
        skippedLabel.innerText = `Files skipped (missing MAC address): ${noMacCnt || 0}`;
        this._summaryPanel.appendChild(skippedLabel);

        let noMatchesLabel = document.createElement("span");
        noMatchesLabel.innerText = `Files with no matching devices: ${noMatchCnt || 0}`;
        this._summaryPanel.appendChild(noMatchesLabel);

        if (results.length > 0) {
            results.forEach((result) => {
                if (result && result.discoveryFilename) {
                    let logMsg = document.createElement("div");
                    logMsg.innerHTML = `${result.discoveryFilename} - ${result.msg}`;
                    logMsg.classList.add("error-msg");
                    this._logList.appendChild(logMsg);
                }
            });
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Handle the user clicking the ok button
     */
    _onOkClick() {
        this._model.sendOk();
    }

    /*******************************************/
    /* END HANDLER METHODS                   */
    /* START PRIVATE API METHODS             */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = UploadSummaryVeilRenderer;
